import type { DocumentShareLinkRecord } from '../services/documentShareLinks.service';

export type VersionTransmissionBadgeState = {
  hasSent: boolean;
  showOutdated: boolean;
  hasNullified: boolean;
};

export function buildVersionTransmissionBadgeState(
  versionId: string,
  links: DocumentShareLinkRecord[]
): VersionTransmissionBadgeState {
  const scoped = links
    .filter((link) => String(link.sentVersionId || '').trim() === String(versionId || '').trim())
    .sort((a, b) => Date.parse(String(b.sentAt || '')) - Date.parse(String(a.sentAt || '')));
  const activeLinks = scoped.filter((link) => !link.revokedAt);
  const latestActive = activeLinks[0] || null;
  const latestLink = scoped[0] || null;
  return {
    hasSent: scoped.length > 0,
    showOutdated: !!latestActive?.isOutdated,
    hasNullified: !!latestLink?.revokedAt,
  };
}

/**
 * Compare the open working quote to the latest customer transmission.
 *
 * Immutable Transmit clones a snapshot (`sentVersionId`) from the working record
 * (`sourceVersionId`). Unlock forks a new working copy from that source — a pristine
 * fork still matches until it diverges (edits).
 */
export function buildDocumentTransmissionVersionInfo(args: {
  activeVersionId: string | null;
  activeVersionNumber: number | null;
  activeIsTransmitted?: boolean;
  activeOverrideUnlocked?: boolean;
  /** Parent version this working copy was cloned/forked from (unlock). */
  activeClonedFromVersionId?: string | null;
  /** True once the fork has been edited. */
  activeDivergedFromClone?: boolean;
  shareLinks: DocumentShareLinkRecord[];
}) {
  const latestTransmitted = [...(args.shareLinks || [])]
    .filter((row) => !row.revokedAt)
    .sort((a, b) => Date.parse(String(b.sentAt || '')) - Date.parse(String(a.sentAt || '')))[0] || null;

  if (!latestTransmitted) {
    return {
      hasTransmission: false,
      hasMismatch: false,
      relation: 'same' as const,
      activeVersionNumber: args.activeVersionNumber,
      transmittedVersionNumber: null as number | null,
      transmittedAt: null as string | null,
    };
  }

  const transmittedVersionId = String(latestTransmitted.sentVersionId || '').trim() || null;
  const sourceVersionId = String(latestTransmitted.sourceVersionId || '').trim() || null;
  const transmittedVersionNumberRaw = Number(latestTransmitted.sentVersionNumber);
  const transmittedVersionNumber = Number.isFinite(transmittedVersionNumberRaw)
    ? Math.trunc(transmittedVersionNumberRaw)
    : null;

  const activeId = String(args.activeVersionId || '').trim() || null;
  const clonedFrom = String(args.activeClonedFromVersionId || '').trim() || null;
  const diverged = !!args.activeDivergedFromClone;
  let hasMismatch = false;

  if (activeId && sourceVersionId) {
    if (activeId === sourceVersionId) {
      hasMismatch = false;
    } else if (clonedFrom === sourceVersionId && !diverged) {
      // Unlock fork of the transmission source, no edits yet.
      hasMismatch = false;
    } else {
      hasMismatch = true;
    }
  } else if (activeId && transmittedVersionId) {
    if (activeId === transmittedVersionId) {
      // Legacy in-place transmit (working row was the customer pin).
      hasMismatch = false;
    } else if (
      args.activeIsTransmitted
      && !args.activeOverrideUnlocked
      && args.activeVersionNumber != null
      && transmittedVersionNumber != null
      && transmittedVersionNumber > args.activeVersionNumber
    ) {
      // Fallback for links minted before source_version_id existed:
      // locked working + newer snapshot pin ⇒ still considered in sync.
      hasMismatch = false;
    } else if (clonedFrom && !diverged && args.activeVersionNumber != null
      && transmittedVersionNumber != null
      && transmittedVersionNumber > args.activeVersionNumber) {
      // Pristine unlock fork without source_version_id on the share link.
      hasMismatch = false;
    } else {
      hasMismatch = true;
    }
  } else if (args.activeVersionNumber != null && transmittedVersionNumber != null) {
    hasMismatch = args.activeVersionNumber !== transmittedVersionNumber;
  }

  let relation: 'active_newer' | 'active_older' | 'different' | 'same' = 'same';
  if (hasMismatch) {
    if (args.activeVersionNumber != null && transmittedVersionNumber != null) {
      relation =
        args.activeVersionNumber > transmittedVersionNumber
          ? 'active_newer'
          : args.activeVersionNumber < transmittedVersionNumber
            ? 'active_older'
            : 'different';
    } else {
      relation = 'different';
    }
  }

  return {
    hasTransmission: true,
    hasMismatch,
    relation,
    activeVersionNumber: args.activeVersionNumber,
    transmittedVersionNumber,
    transmittedAt: latestTransmitted.sentAt || null,
  };
}
